// v6 — plain-language vs technical-language toggle.
//
// Mum (a fantazik with aphantasia) finds technical jargon a barrier; her son Ty
// (with aphantasia) prefers technical precision. We let the user pick
// either mode at the app level via the LanguageToggle in the header;
// downstream components consume `useLanguage()` and switch labels,
// tooltips, and preset descriptions accordingly.
//
// v6-polish — language state now lives in `usePreferences()` (single
// source of truth for all persisted preferences, stored under
// `stamps:prefs`). This file is a thin adapter: `useLanguage` reads the
// lang field from preferences and exposes the same `{ lang, setLang,
// toggle }` shape every consumer already uses. `LanguageProvider` is
// kept as a no-op compatibility wrapper so call sites that import it
// (e.g. App.tsx) continue to work — it's a reminder that the prefs
// hierarchy is mounted even if a future refactor removes the import.

import { type ReactNode } from 'react';
import { usePreferences, type Lang } from './preferences';

export type { Lang };
export const DEFAULT_LANG: Lang = 'plain';

interface LanguageContextValue {
  lang: Lang;
  setLang: (l: Lang) => void;
  toggle: () => void;
}

export function LanguageProvider(props: { children: ReactNode }) {
  // PreferencesProvider is mounted higher in the tree (App.tsx). All this
  // provider does is document that the language context lives there.
  return props.children;
}

export function useLanguage(): LanguageContextValue {
  const { prefs, setLang } = usePreferences();
  return {
    lang: prefs.lang,
    setLang,
    toggle: () => setLang(prefs.lang === 'plain' ? 'technical' : 'plain'),
  };
}

/**
 * Resolve a value that may be a plain string OR a per-mode pair. Keeps
 * call sites concise: `pick(lang, { plain: '…', technical: '…' })` or
 * `pick(lang, 'always the same')`.
 */
export function pick<T>(
  lang: Lang,
  value: T | { plain: T; technical: T },
): T {
  if (
    value !== null &&
    typeof value === 'object' &&
    'plain' in (value as object) &&
    'technical' in (value as object)
  ) {
    const pair = value as { plain: T; technical: T };
    return pair[lang];
  }
  return value as T;
}